// Fechas de las firmas del PDF de EdP.
//
// La API corre en UTC y manda las fechas sin zona ("2026-11-04T21:12:04.123"). Antes el PDF
// mostraba esa hora tal cual (y la firma en mes/día/año, el formato del servidor), así que no
// coincidía ni con la hora de Perú ni con la de Chile, y después de las 21:00 de Chile la fila
// "Fecha" mostraba el día siguiente. Aquí se leen como UTC y se muestran en la hora local de
// quien exporta, en día/mes/año y con la zona escrita (mismo criterio que el PDF de Book).
//
// `timeZone` es opcional: sin él se usa la zona del navegador. Las pruebas lo fijan.

const TIENE_ZONA = /(Z|[+-]\d{2}:?\d{2})$/i;

export const parseFechaUtc = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const texto = String(value).trim().replace(" ", "T");
  const fecha = new Date(TIENE_ZONA.test(texto) ? texto : `${texto}Z`);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

const partesLocales = (fecha, timeZone) => {
  const formato = new Intl.DateTimeFormat("es-CL", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  return Object.fromEntries(
    formato.formatToParts(fecha).map((parte) => [parte.type, parte.value]),
  );
};

/** "2026-11-04" en hora local (para los formatos que ya usaban año-mes-día). */
export const formatFechaISOLocal = (value, timeZone) => {
  const fecha = parseFechaUtc(value);
  if (!fecha) return "";
  const p = partesLocales(fecha, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
};

/** "04/11/2026 16:12" (o con segundos) en hora local. */
export const formatFechaHoraLocal = (value, { segundos = false, timeZone } = {}) => {
  const fecha = parseFechaUtc(value);
  if (!fecha) return "";
  const p = partesLocales(fecha, timeZone);
  const hora = segundos ? `${p.hour}:${p.minute}:${p.second}` : `${p.hour}:${p.minute}`;
  return `${p.day}/${p.month}/${p.year} ${hora}`;
};

const offsetGmt = (fecha, timeZone) => {
  const p = partesLocales(fecha, timeZone);
  const comoUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const minutos = Math.round((comoUtc - fecha.getTime()) / 60000);
  const signo = minutos >= 0 ? "+" : "-";
  const abs = Math.abs(minutos);
  const horas = Math.floor(abs / 60);
  const resto = abs % 60;
  return `GMT${signo}${horas}${resto ? `:${String(resto).padStart(2, "0")}` : ""}`;
};

// Mismos nombres que el Excel (API.Core, ZonaHorariaExport).
const PAIS_POR_ZONA = {
  "America/Lima": "Perú",
  "America/Santiago": "Chile",
  "America/Punta_Arenas": "Chile",
  "America/Bogota": "Colombia",
  "America/Sao_Paulo": "Brasil",
  "America/Argentina/Buenos_Aires": "Argentina",
  "America/Buenos_Aires": "Argentina",
  "America/Mexico_City": "México",
};

/** Zona IANA del navegador (p. ej. "America/Lima"); la usa también el Excel. */
export const zonaHorariaNavegador = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

/** Nombre de la zona en esa fecha, p. ej. "hora de Perú, GMT-5". */
export const nombreZonaLocal = (value, timeZone) => {
  const fecha = parseFechaUtc(value);
  if (!fecha) return "";
  const gmt = offsetGmt(fecha, timeZone);
  const pais = PAIS_POR_ZONA[timeZone || zonaHorariaNavegador()];
  if (pais) return `hora de ${pais}, ${gmt}`;
  try {
    const nombre = new Intl.DateTimeFormat("es", { timeZone, timeZoneName: "long" })
      .formatToParts(fecha)
      .find((parte) => parte.type === "timeZoneName")?.value;
    return nombre && !nombre.startsWith("GMT") ? `${nombre}, ${gmt}` : gmt;
  } catch {
    return gmt;
  }
};

/** Texto de la firma: "Firmado digitalmente por <nombre> (<cargo>) 04/11/2026 16:12:04 (<zona>)". */
export const textoFirmaDigital = ({ nombre, cargo, fecha, timeZone }) => {
  const fechaHora = formatFechaHoraLocal(fecha, { segundos: true, timeZone });
  if (!nombre || !fechaHora) return "";
  const cargoTexto = cargo ? ` (${cargo})` : "";
  return `Firmado digitalmente por ${String(nombre).trim()}${cargoTexto} ${fechaHora} (${nombreZonaLocal(fecha, timeZone)})`;
};
